import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Chip,
  Divider,
  Button,
  Snackbar,
} from '@mui/material';
import { ContentCopy as CopyIcon, DeleteOutline as ClearIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { DUALA_SPECIAL_CHARS, SHORTCUT_MAP, shortcutKeyFromEvent } from '../../data/dualaSpecialChars';
import { dictionaryService } from '../../services/api';

const DRAFT_KEY = 'duala-editor-draft';

// La palette de caractères se colle juste sous le bandeau sticky de PublicPortalLayout.
// Sa hauteur réelle (qui varie : ordinateur, téléphone, bannière sur 1 à 5 lignes) est
// publiée par le layout dans --portal-sticky-height ; 214px (valeur mesurée sur
// ordinateur) ne sert que de repli si la variable est absente.
const HEADER_STICKY_OFFSET = 'var(--portal-sticky-height, 214px)';

// Au-delà de cette longueur (une fois encodé dans le lien), WhatsApp peut tronquer le
// texte pré-rempli : on le copie alors dans le presse-papiers à la place.
const MAX_SHARE_LINK_TEXT = 3500;

const STRIP_EDGES_RE = /^[^\p{L}]+|[^\p{L}]+$/gu;
const cleanWord = (w) => w.replace(STRIP_EDGES_RE, '');

// Certaines entrées du dictionnaire regroupent plusieurs formes dans un seul champ
// (ex. "dongo, mongo") mais aussi des annexes non lexicales à ne jamais proposer
// comme mot insérable : un segment qui commence par un tiret est un suffixe
// grammatical abrégé ("aba, -í", "mōngó, -"), pas un mot ; le contenu entre
// parenthèses est une annotation (sens, dialecte, renvoi : "ábà, -ì (ndóngo)").
// Même logique que wordFormsFromDuala() côté backend (dictionary.routes.js).
function splitWordForms(duala) {
  return duala
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && !s.startsWith('-'))
    .map((s) => s.replace(/\([^)]*\)/g, '').trim())
    .filter((s) => /\p{L}/u.test(s));
}

function expandSuggestionEntries(entries) {
  const seen = new Set();
  const result = [];
  for (const e of entries) {
    for (const form of splitWordForms(e.duala)) {
      const key = form.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ duala: form, french: e.french });
    }
  }
  return result;
}

// Renvoie les bornes du mot (suite de caractères non-espaces) qui contient la position donnée.
function getWordBoundsAt(text, pos) {
  let start = pos;
  while (start > 0 && !/\s/.test(text[start - 1])) start--;
  let end = pos;
  while (end < text.length && !/\s/.test(text[end])) end++;
  return { start, end, word: text.slice(start, end) };
}

const EDITOR_FONT_SX = {
  fontSize: '1.1rem',
  lineHeight: 1.6,
  padding: '16.5px 14px',
  boxSizing: 'border-box',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'inherit',
  margin: 0,
};

// Rouge ondulé : mot introuvable. Orange pointillé : forme conjuguée probable, que le
// dictionnaire (qui ne liste que les verbes à l'infinitif) ne permet pas de confirmer.
const UNKNOWN_STYLE = { textDecorationLine: 'underline', textDecorationStyle: 'wavy', textDecorationColor: '#d32f2f', textDecorationThickness: '2px' };
const UNCERTAIN_STYLE = { textDecorationLine: 'underline', textDecorationStyle: 'dotted', textDecorationColor: '#ed6c02', textDecorationThickness: '2px' };

function renderHighlighted(text, unknownWords, uncertainWords) {
  return text.split(/(\s+)/).map((tok, i) => {
    if (tok === '' || /^\s+$/.test(tok)) return tok;
    const clean = cleanWord(tok).toLowerCase();
    if (clean.length >= 2 && unknownWords.has(clean)) return <span key={i} style={UNKNOWN_STYLE}>{tok}</span>;
    if (clean.length >= 2 && uncertainWords.has(clean)) return <span key={i} style={UNCERTAIN_STYLE}>{tok}</span>;
    return <span key={i}>{tok}</span>;
  });
}

function DualaEditor() {
  const [text, setText] = useState(() => {
    try {
      return localStorage.getItem(DRAFT_KEY) || '';
    } catch {
      return '';
    }
  });
  const [unknownWords, setUnknownWords] = useState(new Set());
  const [uncertainWords, setUncertainWords] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionLabel, setSuggestionLabel] = useState('Suggestions :');
  const [copied, setCopied] = useState(false);
  const [shareNotice, setShareNotice] = useState('');
  const [cursorVersion, setCursorVersion] = useState(0);

  const textareaRef = useRef(null);
  const backdropRef = useRef(null);
  const cursorRef = useRef({ start: 0, end: 0 });
  const suggestionRangeRef = useRef({ start: 0, end: 0 });

  const trackCursor = () => {
    const el = textareaRef.current;
    if (el) {
      cursorRef.current = { start: el.selectionStart, end: el.selectionEnd };
      setCursorVersion((v) => v + 1);
    }
  };

  const setCursorLater = (pos) => {
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
        cursorRef.current = { start: pos, end: pos };
      }
    }, 0);
  };

  // Sauvegarde le brouillon pour qu'il survive à un changement d'onglet ou un rechargement.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, text);
    } catch {
      // stockage indisponible (navigation privée...) : on continue sans persister
    }
  }, [text]);

  // Vérifie, après une pause de frappe, quels mots du texte sont absents du dictionnaire.
  useEffect(() => {
    const t = setTimeout(() => {
      const words = [...new Set(
        text.split(/\s+/).map((w) => cleanWord(w).toLowerCase()).filter((w) => w.length >= 2)
      )];
      if (words.length === 0) {
        setUnknownWords(new Set());
        setUncertainWords(new Set());
        return;
      }
      dictionaryService.checkWords(words)
        .then((res) => {
          setUnknownWords(new Set(res.data.unknown));
          setUncertainWords(new Set(res.data.uncertain || []));
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [text]);

  // Met à jour les suggestions selon le mot au niveau du curseur : liste de correction si le
  // mot est déjà signalé comme inconnu, sinon suggestion d'autocomplétion pendant la frappe.
  useEffect(() => {
    const t = setTimeout(() => {
      const pos = cursorRef.current.start;
      const { start, end, word } = getWordBoundsAt(text, pos);
      const clean = cleanWord(word);
      const lower = clean.toLowerCase();

      if (clean.length < 2) {
        setSuggestions([]);
        return;
      }

      const isUnknown = unknownWords.has(lower);
      const isUncertain = !isUnknown && uncertainWords.has(lower);

      if (isUnknown || isUncertain) {
        suggestionRangeRef.current = { start, end };
        // Forme conjuguée probable : on cherche l'infinitif en retirant la voyelle finale
        // (bolí → bol → bola), puisque le dictionnaire ne liste que cette forme.
        const query = isUncertain && clean.length >= 4 ? clean.slice(0, -1) : clean;
        setSuggestionLabel(isUncertain
          ? `« ${clean} » est peut-être une forme conjuguée. Mots proches :`
          : `Corrections possibles pour « ${clean} » :`);
        // field: 'duala' — on corrige de l'orthographe duala, jamais une traduction : sans ça,
        // la recherche remontait aussi des mots duala sans rapport dont la DEFINITION francaise
        // contenait par hasard une sous-chaine proche (collation MySQL insensible aux accents).
        dictionaryService.search(query, { limit: 6, field: 'duala', track: 0 })
          .then((res) => setSuggestions(expandSuggestionEntries(res.data.entries)))
          .catch(() => setSuggestions([]));
      } else {
        const prefix = text.slice(start, pos);
        if (prefix.trim().length < 2) {
          setSuggestions([]);
          return;
        }
        suggestionRangeRef.current = { start, end: pos };
        setSuggestionLabel('Suggestions :');
        dictionaryService.autocomplete(prefix)
          .then((res) => setSuggestions(expandSuggestionEntries(res.data.words)))
          .catch(() => setSuggestions([]));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [text, cursorVersion, unknownWords, uncertainWords]);

  const insertAtCursor = (insert) => {
    const { start, end } = cursorRef.current;
    const next = text.slice(0, start) + insert + text.slice(end);
    setText(next);
    setCursorLater(start + insert.length);
  };

  const applySuggestion = (word) => {
    const { start, end } = suggestionRangeRef.current;
    const remainder = text.slice(end);
    const needsSpace = remainder.length === 0 || !/^\s/.test(remainder);
    const next = text.slice(0, start) + word + (needsSpace ? ' ' : '') + remainder;
    setText(next);
    setSuggestions([]);
    // Le mot vient d'une entrée réelle du dictionnaire : on retire tout de suite le
    // soulignement sans attendre le prochain cycle de vérification (~600ms).
    const forgetWord = (prev) => {
      const updated = new Set(prev);
      updated.delete(word.toLowerCase());
      return updated;
    };
    setUnknownWords(forgetWord);
    setUncertainWords(forgetWord);
    setCursorLater(start + word.length + (needsSpace ? 1 : 0));
  };

  const handleKeyDown = (e) => {
    const key = shortcutKeyFromEvent(e);
    const char = SHORTCUT_MAP[key];
    if (char) {
      e.preventDefault();
      trackCursor();
      insertAtCursor(char);
    }
  };

  const handleScroll = () => {
    const ta = textareaRef.current;
    const bd = backdropRef.current;
    if (ta && bd) {
      bd.scrollTop = ta.scrollTop;
      bd.scrollLeft = ta.scrollLeft;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
      .finally(() => setTimeout(() => setCopied(false), 2000));
  };

  // Ouvre WhatsApp avec le texte déjà saisi : l'utilisateur choisit ensuite le contact.
  const handleShareWhatsApp = async () => {
    const message = text.trim();
    if (!message) return;
    const encoded = encodeURIComponent(message);
    if (encoded.length <= MAX_SHARE_LINK_TEXT) {
      window.open(`https://wa.me/?text=${encoded}`, '_blank', 'noopener,noreferrer');
      return;
    }
    // Texte trop long pour tenir dans un lien : copie + WhatsApp vide
    try {
      await navigator.clipboard.writeText(message);
      setShareNotice('Texte long : il est copié. Collez-le dans la conversation WhatsApp.');
    } catch {
      setShareNotice('Texte trop long pour être pré-rempli. Utilisez « Copier le texte », puis collez-le dans WhatsApp.');
    }
    window.open('https://wa.me/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
        ✍️ Éditeur de texte douala
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Écrivez en douala avec l'aide des caractères spéciaux ci-dessous. Les mots absents du
        dictionnaire sont soulignés en rouge ; un soulignement orange en pointillés signale une
        forme peut-être conjuguée, que le dictionnaire ne peut pas confirmer. Cliquez dans un mot
        souligné pour voir des suggestions. Les tons ne sont pas obligatoires : « mboa » est
        reconnu comme « mbóa ». Survolez un caractère pour voir son raccourci clavier.
      </Typography>

      {/* Collant sous le bandeau du site (AppBar + onglets + bannière WhatsApp, eux-memes
          sticky) : sans ca, la palette de caracteres speciaux disparaissait au scroll des
          qu'on tapait plus de quelques lignes, avec pour seul indice une trace bleue en
          haut de page (le focus ring du premier bouton) — on aurait facilement pu croire
          qu'elle avait disparu. */}
      <Box
        sx={{
          position: 'sticky',
          top: HEADER_STICKY_OFFSET,
          zIndex: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          gap: 0.5,
          // Téléphone : une seule ligne qui défile à l'horizontale (20 caractères sur
          // 3 lignes mangeaient l'écran, surtout clavier ouvert) ; ordinateur : retour à la ligne.
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          overflowX: { xs: 'auto', sm: 'visible' },
          WebkitOverflowScrolling: 'touch',
          mb: { xs: 2, sm: 3 },
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        }}
      >
        {DUALA_SPECIAL_CHARS.map((c) => (
          <Tooltip
            key={c.char}
            title={`${c.name}${c.tone ? ` (${c.tone})` : ''}${c.shortcut ? ` — raccourci : ${c.shortcut}` : ' — clic uniquement'}`}
            arrow
            describeChild
          >
            <Button
              variant="outlined"
              size="small"
              aria-label={`${c.char} ${c.name}${c.tone ? ` (${c.tone})` : ''}`}
              onClick={() => { trackCursor(); insertAtCursor(c.char); }}
              sx={{ minWidth: 40, flexShrink: 0, fontSize: '1.1rem', fontWeight: 600, textTransform: 'none' }}
            >
              {c.char}
            </Button>
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ position: 'relative' }}>
        <Box
          ref={backdropRef}
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            color: 'transparent',
            border: '1px solid transparent',
            borderRadius: 1,
            ...EDITOR_FONT_SX,
          }}
        >
          {renderHighlighted(text, unknownWords, uncertainWords)}
        </Box>
        <Box
          component="textarea"
          ref={textareaRef}
          rows={10}
          placeholder="Écrivez votre texte en douala ici..."
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onSelect={trackCursor}
          onClick={trackCursor}
          onKeyUp={trackCursor}
          onScroll={handleScroll}
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            resize: 'none',
            background: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            outline: 'none',
            '&:focus': { borderColor: 'primary.main' },
            ...EDITOR_FONT_SX,
          }}
        />
      </Box>

      {suggestions.length > 0 && (
        // Collant en bas de l'ecran (pas seulement du textarea) : en pleine frappe,
        // la page a souvent defile (palette + zone de texte hautes) et les
        // suggestions se retrouvaient hors-champ sous le pli, invisibles sans
        // scroller manuellement pendant qu'on tape.
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 1,
            py: 1,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
            zIndex: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {suggestionLabel}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {suggestions.map((s) => (
              <Tooltip key={s.duala} title={s.french} arrow>
                <Chip label={s.duala} onClick={() => applySuggestion(s.duala)} clickable />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<CopyIcon />} onClick={handleCopy}>
          {copied ? 'Copié !' : 'Copier le texte'}
        </Button>
        <Button
          variant="contained"
          startIcon={<WhatsAppIcon />}
          onClick={handleShareWhatsApp}
          disabled={!text.trim()}
          sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe5b' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
        >
          Partager sur WhatsApp
        </Button>
        <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={() => setText('')}>
          Effacer
        </Button>
      </Box>

      <Snackbar
        open={Boolean(shareNotice)}
        autoHideDuration={5000}
        onClose={() => setShareNotice('')}
        message={shareNotice}
      />
    </Paper>
  );
}

export default DualaEditor;
